/**
 * Integrated Circuit Breaker Safety Framework
 *
 * Combines circuit breaker protection with life-critical safety validation
 * to ensure system reliability and safety preservation under all conditions.
 *
 * Features:
 * - Circuit breaker state monitoring with safety score correlation
 * - Emergency fallback when circuit breakers trip
 * - Safety-aware load shedding and graceful degradation
 * - Real-time safety score impact assessment
 * - Compliance framework validation during circuit breaker events
 */
// @ts-nocheck


import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
// Import CircuitBreakerLink type - temporarily declare interface until proper export
interface CircuitBreakerLink {
  getCircuitBreakerStates(): Map<string, any>;
  forceCircuitBreakerState(operationName: string, state: 'CLOSED' | 'OPEN' | 'HALF_OPEN'): void;
}
import {
    Alert,
    Badge,
    Button,
    Card,
    Grid,
    Group,
    Modal,
    Paper,
    Progress,
    Stack,
    Text
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
    IconAlertTriangle,
    IconEmergencyBed,
    IconRefresh,
    IconShield,
    IconShieldCheck
} from '@tabler/icons-react';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import useMetadataRepository from '../hooks/useMetadataRepository';

// GraphQL query for circuit breaker safety metrics
const GET_CIRCUIT_BREAKER_SAFETY_METRICS = gql`
  query GetCircuitBreakerSafetyMetrics($tenant: String!) {
    getCircuitBreakerSafetyMetrics(tenant: $tenant) {
      circuitBreakerStates {
        name
        state
        failureCount
        lastFailureTime
        safetyImpactScore
      }
      overallSafetyScore
      emergencyModeActive
      complianceFrameworksAffected
      loadSheddingActive
      fallbackPrimitivesActive
    }
  }
`;

// Types
interface CircuitBreakerState {
  name: string;
  state: 'CLOSED' | 'OPEN' | 'HALF_OPEN';
  failureCount: number;
  lastFailureTime: number;
  safetyImpactScore: number;
}

interface SafetyMetrics {
  circuitBreakerStates: CircuitBreakerState[];
  overallSafetyScore: number;
  emergencyModeActive: boolean;
  complianceFrameworksAffected: string[];
  loadSheddingActive: boolean;
  fallbackPrimitivesActive: string[];
}

interface SafetyFrameworkConfig {
  safetyThreshold: number;
  circuitBreakerFailureThreshold: number;
  emergencyModeTimeoutMs: number;
  complianceValidationEnabled: boolean;
  loadSheddingEnabled: boolean;
}

interface IntegratedSafetyContextType {
  safetyMetrics: SafetyMetrics | null;
  safetyConfig: SafetyFrameworkConfig;
  circuitBreakerLink: CircuitBreakerLink | null;
  emergencyMode: boolean;
  activateSafetyFallback: () => void;
  resetCircuitBreakers: () => void;
  updateSafetyThreshold: (threshold: number) => void;
}

const IntegratedSafetyContext = createContext<IntegratedSafetyContextType | null>(null);

export const useIntegratedSafety = () => {
  const context = useContext(IntegratedSafetyContext);
  if (!context) {
    throw new Error('useIntegratedSafety must be used within IntegratedCircuitBreakerSafetyFramework');
  }
  return context;
};

interface IntegratedCircuitBreakerSafetyFrameworkProps {
  tenant: string;
  children: React.ReactNode;
  circuitBreakerLink?: CircuitBreakerLink;
  onEmergencyModeChange?: (active: boolean) => void;
  onSafetyScoreChange?: (score: number) => void;
}

export const IntegratedCircuitBreakerSafetyFramework: React.FC<IntegratedCircuitBreakerSafetyFrameworkProps> = ({
  tenant,
  children,
  circuitBreakerLink,
  onEmergencyModeChange,
  onSafetyScoreChange
}) => {
  const [emergencyMode, setEmergencyMode] = useState(false);
  const [showSafetyModal, setShowSafetyModal] = useState(false);
  const [safetyConfig, setSafetyConfig] = useState<SafetyFrameworkConfig>({
    safetyThreshold: 70,
    circuitBreakerFailureThreshold: 3,
    emergencyModeTimeoutMs: 300000, // 5 minutes
    complianceValidationEnabled: true,
    loadSheddingEnabled: true
  });

  // Load tenant-specific safety thresholds
  const metadataRepoAny = useMetadataRepository() as any;
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const t = await metadataRepoAny.getSafetyThresholds(tenant);
        if (mounted && t?.WARNING) {
          setSafetyConfig(prev => ({ ...prev, safetyThreshold: t.WARNING }));
        }
      } catch (_: any) {}
    })();
    return () => { mounted = false; };
  }, [tenant, metadataRepoAny]);

  // Load safety metrics
  const {
    data: metricsData,
    loading: metricsLoading,
    error: metricsError,
    refetch: refetchMetrics
  } = useQuery<{ getCircuitBreakerSafetyMetrics: SafetyMetrics }>(GET_CIRCUIT_BREAKER_SAFETY_METRICS, {
    variables: { tenant },
    pollInterval: 5000, // Poll every 5 seconds for real-time monitoring
    errorPolicy: 'all',
    onCompleted: (data: any) => {
      const metrics = data?.getCircuitBreakerSafetyMetrics;

      // Monitor safety score changes
      if (onSafetyScoreChange) {
        onSafetyScoreChange(metrics.overallSafetyScore);
      }

      // Check for emergency conditions
      if (metrics.overallSafetyScore < safetyConfig.safetyThreshold) {
        if (!emergencyMode) {
          activateEmergencyMode('SAFETY_SCORE_CRITICAL');
        }
      }

      // Monitor circuit breaker failures
      const openCircuitBreakers = metrics.circuitBreakerStates.filter((cb: any) => cb.state === 'OPEN');
      if (openCircuitBreakers.length >= safetyConfig.circuitBreakerFailureThreshold) {
        if (!emergencyMode) {
          activateEmergencyMode('CIRCUIT_BREAKER_THRESHOLD_EXCEEDED');
        }
      }
    }
  });

  const safetyMetrics = metricsData?.getCircuitBreakerSafetyMetrics || null;

  // Emergency mode activation
  const activateEmergencyMode = useCallback((reason: string) => {
    setEmergencyMode(true);
    onEmergencyModeChange?.(true);

    notifications.show({
      id: 'emergency-mode',
      title: 'Emergency Mode Activated',
      message: `System activated emergency mode: ${reason}`,
      color: 'red',
      icon: <IconEmergencyBed size={16} />,
      autoClose: false
    });

    console.error('Emergency mode activated:', {
      reason,
      timestamp: new Date().toISOString(),
      tenant,
      safetyScore: safetyMetrics?.overallSafetyScore
    });

    // Auto-deactivate after timeout
    setTimeout(() => {
      if (safetyMetrics?.overallSafetyScore && safetyMetrics.overallSafetyScore >= safetyConfig.safetyThreshold) {
        deactivateEmergencyMode('TIMEOUT_RECOVERY');
      }
    }, safetyConfig.emergencyModeTimeoutMs);
  }, [safetyMetrics, safetyConfig, tenant, onEmergencyModeChange]);

  const deactivateEmergencyMode = useCallback((reason: string) => {
    setEmergencyMode(false);
    onEmergencyModeChange?.(false);

    notifications.hide('emergency-mode');
    notifications.show({
      id: 'emergency-recovery',
      title: 'Emergency Mode Deactivated',
      message: `System recovered: ${reason}`,
      color: 'green',
      icon: <IconShieldCheck size={16} />,
      autoClose: 5000
    });

    console.info('Emergency mode deactivated:', {
      reason,
      timestamp: new Date().toISOString(),
      tenant
    });
  }, [tenant, onEmergencyModeChange]);

  // Safety fallback activation
  const activateSafetyFallback = useCallback(() => {
    // Force circuit breakers to minimal primitive set
    if (circuitBreakerLink) {
      const essentialOperations = ['getDraft', 'listDrafts', 'workflowExecution'];

      // Close essential operations
      essentialOperations.forEach(op => {
        circuitBreakerLink.forceCircuitBreakerState(op, 'CLOSED');
      });

      console.info('Safety fallback activated - essential operations only');
    }

    notifications.show({
      title: 'Safety Fallback Active',
      message: 'System operating with minimal primitive set for safety preservation',
      color: 'yellow',
      icon: <IconShield size={16} />
    });
  }, [circuitBreakerLink]);

  // Reset circuit breakers
  const resetCircuitBreakers = useCallback(() => {
    if (circuitBreakerLink) {
      const states = circuitBreakerLink.getCircuitBreakerStates();

      states.forEach((_: any, operationName: string) => {
        circuitBreakerLink.forceCircuitBreakerState(operationName, 'CLOSED');
      });

      console.info('All circuit breakers reset to CLOSED state');

      notifications.show({
        title: 'Circuit Breakers Reset',
        message: 'All circuit breakers have been reset to closed state',
        color: 'blue',
        icon: <IconRefresh size={16} />
      });
    }

    refetchMetrics();
  }, [circuitBreakerLink, refetchMetrics]);

  // Update safety threshold
  const updateSafetyThreshold = useCallback((threshold: number) => {
    setSafetyConfig(prev => ({
      ...prev,
      safetyThreshold: threshold
    }));

    console.info('Safety threshold updated:', threshold);
  }, []);

  // Context value
  const contextValue: IntegratedSafetyContextType = {
    safetyMetrics,
    safetyConfig,
    circuitBreakerLink: circuitBreakerLink || null,
    emergencyMode,
    activateSafetyFallback,
    resetCircuitBreakers,
    updateSafetyThreshold
  };

  // Safety status color
  const getSafetyStatusColor = (score: number) => {
    const warn = safetyConfig.safetyThreshold;
    const excellent = Math.max(warn + 10, 90);
    if (score < warn) return 'red';
    if (score < excellent) return 'yellow';
    if (score < excellent + 10) return 'blue';
    return 'green';
  };

  // Render safety dashboard modal
  const renderSafetyModal = () => (
    <Modal
      opened={showSafetyModal}
      onClose={() => setShowSafetyModal(false)}
      title="Integrated Safety & Circuit Breaker Dashboard"
      size="xl"
    >
      {safetyMetrics && (
        <Stack gap="md">
          {/* Overall safety score */}
          <Paper p="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Text fw={600}>Overall Safety Score</Text>
              <Badge color={getSafetyStatusColor(safetyMetrics.overallSafetyScore)} size="lg">
                {safetyMetrics.overallSafetyScore}/100
              </Badge>
            </Group>
            <Progress
              value={safetyMetrics.overallSafetyScore}
              color={getSafetyStatusColor(safetyMetrics.overallSafetyScore)}
              size="lg"
              striped={safetyMetrics.overallSafetyScore! < safetyConfig.safetyThreshold}
              animated={safetyMetrics.overallSafetyScore! < safetyConfig.safetyThreshold}
            />
          </Paper>

          {/* Circuit breaker states */}
          <Paper p="md" withBorder>
            <Text fw={600} mb="md">Circuit Breaker States</Text>
            <Grid>
              {safetyMetrics.circuitBreakerStates?.map(cb => (
                <Grid.Col key={cb.name} span={{ base: 6, md: 4 }}>
                  <Card p="sm" withBorder>
                    <Group justify="space-between" mb="xs">
                      <Text size="sm" fw={500}>{cb.name}</Text>
                      <Badge
                        color={cb.state === 'CLOSED' ? 'green' : cb.state === 'OPEN' ? 'red' : 'yellow'}
                        size="xs"
                      >
                        {cb.state}
                      </Badge>
                    </Group>
                    <Text size="xs" color="dimmed">Failures: {cb.failureCount}</Text>
                    <Text size="xs" color="dimmed">Safety Impact: {cb.safetyImpactScore}</Text>
                  </Card>
                </Grid.Col>
              ))}
            </Grid>
          </Paper>

          {/* Emergency conditions */}
          {(safetyMetrics.emergencyModeActive || emergencyMode) && (
            <Alert
              icon={<IconAlertTriangle size={16} />}
              title="Emergency Mode Active"
              color="red"
            >
              <Stack gap="xs">
                <Text size="sm">System is operating in emergency mode</Text>
                {safetyMetrics.loadSheddingActive && (
                  <Text size="sm">• Load shedding active</Text>
                )}
                {safetyMetrics.fallbackPrimitivesActive?.length > 0 && (
                  <Text size="sm">
                    • Fallback primitives: {safetyMetrics.fallbackPrimitivesActive?.join(', ')}
                  </Text>
                )}
              </Stack>
            </Alert>
          )}

          {/* Compliance frameworks */}
          {safetyMetrics.complianceFrameworksAffected?.length > 0 && (
            <Paper p="md" withBorder>
              <Text fw={600} mb="xs">Affected Compliance Frameworks</Text>
              <Group gap="xs">
                {safetyMetrics.complianceFrameworksAffected?.map(framework => (
                  <Badge key={framework} color="red" variant="light">
                    {framework}
                  </Badge>
                ))}
              </Group>
            </Paper>
          )}

          {/* Actions */}
          <Group>
            <Button
              variant="outline"
              color="blue"
              leftSection={<IconRefresh size={16} />}
              onClick={resetCircuitBreakers}
            >
              Reset Circuit Breakers
            </Button>
            <Button
              variant="outline"
              color="yellow"
              leftSection={<IconShield size={16} />}
              onClick={activateSafetyFallback}
            >
              Activate Safety Fallback
            </Button>
            {emergencyMode && (
              <Button
                variant="outline"
                color="green"
                leftSection={<IconShieldCheck size={16} />}
                onClick={() => deactivateEmergencyMode('MANUAL_OVERRIDE')}
              >
                Deactivate Emergency Mode
              </Button>
            )}
          </Group>
        </Stack>
      )}
    </Modal>
  );

  return (
    <IntegratedSafetyContext.Provider value={contextValue}>
      {/* Safety status indicator */}
      {safetyMetrics && (
        <Paper p="xs" mb="md" withBorder style={{
          borderColor: emergencyMode ? 'red' : getSafetyStatusColor(safetyMetrics.overallSafetyScore),
          backgroundColor: emergencyMode ? '#ffe0e0' : undefined
        }}>
          <Group justify="space-between">
            <Group gap="xs">
              <IconShield size={16} color={emergencyMode ? 'red' : getSafetyStatusColor(safetyMetrics.overallSafetyScore)} />
              <Text size="sm" fw={500}>
                Safety: {safetyMetrics.overallSafetyScore}/100
              </Text>
              {emergencyMode && (
                <Badge color="red" size="sm">EMERGENCY</Badge>
              )}
            </Group>
            <Button
              size="xs"
              variant="subtle"
              onClick={() => setShowSafetyModal(true)}
            >
              Details
            </Button>
          </Group>
        </Paper>
      )}

      {/* Loading state */}
      {metricsLoading && (
        <Alert color="blue" mb="md">
          <Text size="sm">Loading safety metrics...</Text>
        </Alert>
      )}

      {/* Error state */}
      {metricsError && (
        <Alert
          icon={<IconAlertTriangle size={16} />}
          title="Safety Metrics Error"
          color="red"
          mb="md"
        >
          <Text size="sm">{metricsError.message}</Text>
        </Alert>
      )}

      {children}
      {renderSafetyModal()}
    </IntegratedSafetyContext.Provider>
  );
};

export default IntegratedCircuitBreakerSafetyFramework;